import { useEffect, useRef, useState } from 'react';

/** Beyond this the field scrolls internally rather than growing further. */
const MAX_TEXTAREA_HEIGHT = 120;
/** Quiet for this long and the other side stops being told you are typing. */
const TYPING_IDLE_MS = 3000;

type Options = {
  /**
   * The message being edited, if any - its body seeds the draft. Depended on by
   * identity, not by body: picking a second message to edit has to reload the
   * field even in the rare case the two say the same thing.
   */
  editing?: { body: string | null } | null;
  /** The message being replied to, so the field takes focus when one is picked. */
  replyTo?: unknown;
  /** Broadcasts typing state; absent on the merchant side. */
  onTyping?: (typing: boolean) => void;
  /**
   * Opening a conversation puts the cursor in the field - pass its id. Null
   * while no thread is open, which focuses nothing.
   */
  autoFocusKey?: number | string | null;
};

/**
 * A mouse and a keyboard, rather than a screen size.
 *
 * Width is the wrong question for "may I take focus": a tablet in landscape is
 * as wide as a laptop and still answers focus with a keyboard over half the
 * screen. `pointer: fine` is a real pointing device and `hover: hover` rules
 * out the touch screens that emulate one.
 */
const hasKeyboard = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

/**
 * The composer's own text: the draft, the textarea that grows with it, and the
 * typing signal that follows from it.
 *
 * These three are one concern - every one of them is driven by the same string
 * changing - which is why they are a hook rather than three effects sitting in
 * the middle of the component.
 */
export const useComposerDraft = ({
  editing,
  replyTo,
  onTyping,
  autoFocusKey,
}: Options) => {
  const [value, setValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  /*
   * Entering edit mode loads the existing body; leaving it clears the draft.
   *
   * Focus follows only when there IS something to edit. This effect also runs
   * on mount, where `editing` is empty - and taking focus there meant every
   * thread opened with the phone keyboard already up, covering half the
   * conversation the reader came to read before they had asked to write
   * anything.
   */
  useEffect(() => {
    setValue(editing?.body ?? '');
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  /*
   * Opening a conversation leaves the cursor ready to type in it.
   *
   * Desktop only, for the same reason the edit effect above focuses only when
   * there is something to edit: on a phone this would raise the keyboard over
   * the conversation the reader came to read, before they had asked to write
   * anything.
   *
   * `preventScroll`, because the thread is being pinned to its newest message
   * at this very moment - focusing without it hands the browser a second
   * opinion about where the scroll container should be.
   */
  useEffect(() => {
    if (autoFocusKey === null || autoFocusKey === undefined) return;
    if (!hasKeyboard()) return;
    textareaRef.current?.focus({ preventScroll: true });
  }, [autoFocusKey]);

  /*
   * Grow with the content up to a cap, then scroll internally.
   *
   * Re-measured on resize as well as on every keystroke. The field's natural
   * height is not a constant: it is padded taller on a phone so its first line
   * lands on the axis of the bigger touch controls beside it. Measuring only
   * when the draft changes left that height written into the element from
   * whichever side of the breakpoint the window happened to be on - so crossing
   * it with an untouched field kept the old height, and the text sat eight
   * pixels off the icons until something was typed.
   */
  useEffect(() => {
    const resize = () => {
      const element = textareaRef.current;
      if (!element) return;
      element.style.height = 'auto';
      element.style.height = `${Math.min(
        element.scrollHeight,
        MAX_TEXTAREA_HEIGHT
      )}px`;
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [value]);

  /* Tell the other side someone is composing, and stop saying so when the
     draft is emptied or a moment passes with no keystroke. */
  useEffect(() => {
    if (!onTyping) return undefined;
    if (!value) {
      onTyping(false);
      return undefined;
    }
    onTyping(true);
    const timer = window.setTimeout(() => onTyping(false), TYPING_IDLE_MS);
    return () => window.clearTimeout(timer);
  }, [value, onTyping]);

  return { value, setValue, textareaRef };
};
